import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Injector, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { correlationIdFromError } from '../http/correlation-id.interceptor';
import { ApiErrorException, Envelope } from '../models/envelope';
import { TelemetryService } from '../telemetry/app-insights';

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Global client error handler (T-060, Tech plan §2.2/§6/§7). Maps errors to a
 * user-facing message, surfaces them via a snackbar, and forwards exceptions to
 * telemetry with the correlation id.
 */
@Injectable()
export class ErrorHandlerService implements ErrorHandler {
  private readonly injector = inject(Injector);
  private readonly telemetry = inject(TelemetryService);

  handleError(error: unknown): void {
    const message = this.toUserMessage(error);
    this.telemetry.trackException(this.toError(error), this.correlationIdOf(error));
    this.notify(message);
  }

  /** Maps any thrown value to a concise, user-facing message. */
  toUserMessage(error: unknown): string {
    if (error instanceof ApiErrorException) {
      return error.errors[0]?.message ?? DEFAULT_MESSAGE;
    }
    if (error instanceof HttpErrorResponse) {
      const envelope = error.error as Envelope<unknown> | undefined;
      if (envelope?.errors?.length) {
        return envelope.errors[0].message;
      }
      switch (error.status) {
        case 0:
          return 'Unable to reach the server. Check your connection and try again.';
        case 401:
          return 'Your session has expired. Please sign in again.';
        case 403:
          return 'You do not have permission to perform this action.';
        case 404:
          return 'The requested item could not be found.';
        case 409:
          return 'This record was changed by someone else. Reload and try again.';
        default:
          return 'A server error occurred. Please try again.';
      }
    }
    if (error instanceof Error) {
      return error.message || DEFAULT_MESSAGE;
    }
    return DEFAULT_MESSAGE;
  }

  private correlationIdOf(error: unknown): string | undefined {
    if (error instanceof ApiErrorException) {
      return error.correlationId;
    }
    if (error instanceof HttpErrorResponse) {
      return correlationIdFromError(error);
    }
    return undefined;
  }

  private toError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
  }

  private notify(message: string): void {
    try {
      const snackBar = this.injector.get(MatSnackBar);
      const zone = this.injector.get(NgZone);
      zone.run(() => snackBar.open(message, 'Dismiss', { duration: 6000 }));
    } catch {
      // Snackbar not available (e.g. during bootstrap or unit tests) — message already mapped.
    }
  }
}
